import React, { useRef, useCallback, memo } from 'react';
import * as _ from 'lodash';
import { VariableSizeGrid as Grid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Tippy from '../../components/ReactTippy';

import {
  Cell,
  getGridData,
  getScrollbarWidth,
} from '../../utilities/workersCaptureTableUtil';
import { DIALOG_NAME } from '../../pages/WorkersCapture';

/**
 * @description 作業員データ取り込み画面_テーブル
 * @param {array} workerLists 作業員情報リスト
 * @param {boolean} isFileSelect true: ファイル選択済み, false: ファイル未選択
 * @param {boolean} isFileError ファイルエラーフラグ true: エラー有り, false: エラー無し
 * @param {boolean} isLoading true: ロード中, false: ロード完了
 */
export const WorkersCaptureTable = memo(
  ({ workerLists, isFileSelect, isFileError, isLoading, version }) => {
    // テーブルのすべてのデータをarray matrix（つまり、他の多くの配列を含む配列）の形式で取得する
    const chunk = 50;
    let chunkIndex = 0;
    let tableData = [];
    if (_.size(workerLists) === 0) {
      tableData = getGridData([], false, version);
    }
    /**
     * IEでの読み込みデータ処理時間短縮のためデータを分割する
     * IEで扱える配列の大きさの許容量を超えると描画までの時間が大きく増加する
     */
    while (chunkIndex * chunk < _.size(workerLists)) {
      const sliceData = workerLists.slice(
        chunkIndex * chunk,
        (chunkIndex + 1) * chunk
      );
      const tmp = getGridData(
        sliceData,
        isFileError,
        version,
        chunkIndex === 0
      );

      tableData = [...tableData, ...tmp];
      chunkIndex++;
    }

    return (
      <div className='master-slide-view__body WorkersCaptureTable'>
        <div className='constructionlist__list'>
          {!_.isEmpty(tableData) && <VirtualTable data={tableData} />}

          {isLoading && _.isEmpty(workerLists) && <div className='indicator' />}

          {(!isFileSelect || _.isEmpty(workerLists)) && !isLoading && (
            <div className='no-data-text'>
              「ファイルを選択」ボタンを押し、xlsxファイルを選択してください。
            </div>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // ダイアログを閉じたり開いたりするときにテーブルを再レンダリングしない
    return [DIALOG_NAME.registerConfirm, DIALOG_NAME.registerDone].includes(
      nextProps.visibleDialogName
    );
  }
);

const VirtualTable = ({ data }) => {
  const DEFAULT_TABLE_HEIGHT = 600;
  const DATA_ROW_HEIGHT = 40;
  const HEADER_ROW_HEIGHT = 24;
  const headingsGridRef = useRef(null);
  const columnCount = data[0].length;
  const isScrollable =
    data.length >= DEFAULT_TABLE_HEIGHT / DATA_ROW_HEIGHT + 2;
  const scrollbarWidth = getScrollbarWidth();
  const tableHeight = isScrollable
    ? DEFAULT_TABLE_HEIGHT
    : DATA_ROW_HEIGHT * data.length;

  // 各行の列の長さとしてサブヘディング列（表の黒いヘディング列）の長さを使用する
  const getColumnWidth = columnIndex => {
    const subHeadingsRow = data[1];
    const columnWidths = _.map(subHeadingsRow, cell => cell.width);
    return columnWidths[columnIndex];
  };

  // グリッドテーブルに従ってグリッドヘッダーをスクロールさせる
  const syncHorizontalScroll = useCallback(
    ({ scrollLeft, scrollUpdateWasRequested }) => {
      if (!scrollUpdateWasRequested) {
        headingsGridRef.current.scrollTo({ scrollLeft, scrollTop: 0 });
      }
    }
  );

  const headerCellRenderer = memo(({ columnIndex, rowIndex, style }) => {
    const rows = data.slice(0, 2);
    const cell = rows[rowIndex][columnIndex];
    const classNames = {
      [Cell.MAIN_HEADING]: 'table__cell-head--main',
      [Cell.SUB_HEADING]: 'table__cell-head--sub',
      [Cell.BLANK]: 'table__cell-head--blank',
    };
    const cellClassName = `table__cell ${classNames[cell.type]} ${
      cell.type === Cell.SUB_HEADING && columnIndex === 0
        ? 'table__cell--first'
        : ''
    }`;

    return (
      cell && (
        <div style={style} className={cellClassName}>
          {cell.value}
          {cell.type === Cell.SUB_HEADING && cell.required && (
            <span className='required'>必須</span>
          )}
        </div>
      )
    );
  });

  const dataCellRenderer = memo(({ columnIndex, rowIndex, style }) => {
    const rows = data.slice(2);
    const cell = rows[rowIndex][columnIndex];
    const cellClassName = `table__cell table__cell-data ${
      cell.isError ? 'table__cell-data--error' : ''
    } ${columnIndex === 0 ? 'table__cell--first' : ''}`;
    const cellValueClassName = cell.isError
      ? 'cell-value cell-value--error'
      : 'cell-value';

    return (
      cell && (
        <div style={style} className={cellClassName}>
          <Tippy
            duration={10}
            distance={24}
            arrowType='sharp'
            delay={[500, 0]}
            placement='bottom'
            followCursor={true}
            html={
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: '1.5',
                  textAlign: 'left',
                  color: 'white',
                  maxWidth: '400px',
                  wordWrap: 'break-word',
                }}
              >
                {cell.value}
              </div>
            }
          >
            <div className={cellValueClassName}>{cell.value}</div>
          </Tippy>
        </div>
      )
    );
  });

  return (
    <AutoSizer>
      {({ width }) => (
        <React.Fragment>
          {data.length === 2 ? (
            <Grid
              className='table__header'
              ref={headingsGridRef}
              columnCount={columnCount}
              rowCount={2}
              columnWidth={getColumnWidth}
              rowHeight={() => HEADER_ROW_HEIGHT}
              width={width}
              height={HEADER_ROW_HEIGHT * 3}
            >
              {headerCellRenderer}
            </Grid>
          ) : (
            <React.Fragment>
              <Grid
                className='table__header'
                ref={headingsGridRef}
                columnCount={columnCount}
                rowCount={2}
                columnWidth={getColumnWidth}
                rowHeight={() => HEADER_ROW_HEIGHT}
                width={isScrollable ? width - scrollbarWidth : width}
                height={HEADER_ROW_HEIGHT * 2}
              >
                {headerCellRenderer}
              </Grid>

              <Grid
                onScroll={syncHorizontalScroll}
                columnCount={columnCount}
                rowCount={data.length - 2}
                columnWidth={getColumnWidth}
                rowHeight={() => DATA_ROW_HEIGHT}
                width={width}
                height={tableHeight}
              >
                {dataCellRenderer}
              </Grid>
            </React.Fragment>
          )}

          {/* テーブルにデータがない場合にスクロールバーとして操作 */}
          {data.length <= 2 && (
            <Grid
              onScroll={syncHorizontalScroll}
              rowCount={1}
              columnCount={columnCount}
              rowHeight={() => HEADER_ROW_HEIGHT}
              columnWidth={getColumnWidth}
              width={width}
              height={600}
            >
              {() => null}
            </Grid>
          )}
        </React.Fragment>
      )}
    </AutoSizer>
  );
};



// WEBPACK FOOTER //
// ./src/components/tables/WorkersCapture.jsx